import React from 'react';
import { Store, MapPin, ExternalLink, Navigation, CheckCircle2 } from 'lucide-react';
import { StoreInfo } from '../../../../types';
import { useCartAssistant, useCartAssistantInput } from "../../../lib/cartAssistant/CartAssistantProvider";
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../lib/store';

interface StoreLocationCardProps {
  store: StoreInfo;
}

export const StoreLocationCard: React.FC<{ props: StoreLocationCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const { language } = useStore();
  const { store } = props || {};
  const assistantInput = useCartAssistantInput();
  const { thread, cancelRun, isIdle } = useCartAssistant();
  const [pendingText, setPendingText] = React.useState<string | null>(null);

  // RESILIENCE: Check if thread is stuck waiting for tool calls
  const isStuck = React.useMemo(() => {
    const threadAny = thread as any;
    return threadAny?.thread?.status === 'requires_action' || 
           (threadAny?.pendingToolCallIds && threadAny.pendingToolCallIds.length > 0) ||
           (!isIdle && !threadAny?.streaming?.isActive);
  }, [thread, isIdle]);

  React.useEffect(() => {
    const fireSubmit = async (isRetry = false) => {
      if (pendingText && assistantInput.value === pendingText && !assistantInput.isDisabled) {
        try {
          // PROACTIVE RECOVERY: If the thread is stuck waiting for tool calls from a previous run,
          // submit() will throw 400. We cancel the existing run here to ensure success.
          if (isStuck) {
            console.warn("[CartAssistant] [StoreLocationCard] Thread state is stuck. Attempting to cancel run...");
            if (cancelRun) {
              try {
                await cancelRun();
                console.log("[CartAssistant] [StoreLocationCard] Stuck run cancelled proactively.");
                await new Promise(resolve => setTimeout(resolve, 1000));
                // Re-sync value after cancel
                assistantInput.setValue(pendingText);
                await new Promise(resolve => setTimeout(resolve, 300));
              } catch (err) {
                console.error("[CartAssistant] [StoreLocationCard] Proactive cancel failed:", err);
              }
            }
          }

          console.log("[CartAssistant] [StoreLocationCard] API SUBMIT INITIATED", { isRetry });

          await assistantInput.submit();
          setPendingText(null);
        } catch (err: any) {
          const errorMsg = err?.message || String(err);
          const is400 = errorMsg.includes('400') || 
                       errorMsg.includes('MISSING_RESULTS') || 
                       err?.status === 400 ||
                       err?.code === 'MISSING_RESULTS';
          
          console.error(`[CartAssistant] [StoreLocationCard] Submission failed (isRetry: ${isRetry}):`, errorMsg);
          
          if (is400 && !isRetry) {
             const isSdkOutOfSync = isIdle;
             
             if (!isSdkOutOfSync) {
               console.warn("[CartAssistant] [StoreLocationCard] 400 MISSING_RESULTS detected. Attempting auto-recovery retry...");
               try {
                 if (cancelRun) {
                   await cancelRun();
                   console.log("[CartAssistant] [StoreLocationCard] Stuck run cancelled on error catch. Retrying...");
                   await new Promise(resolve => setTimeout(resolve, 1500));
                   // Re-apply the value before retry
                   assistantInput.setValue(pendingText);
                   await new Promise(resolve => setTimeout(resolve, 500));
                   // AUTO-RETRY
                   return await fireSubmit(true);
                 }
               } catch (recoveryErr) {
                 console.error("[CartAssistant] [StoreLocationCard] Recovery retry failed:", recoveryErr);
               }
             } else {
               console.warn("[CartAssistant] [StoreLocationCard] SDK state desync detected (Idle but backend waiting for tool). Skipping futile retry.");
             }
          }
          setPendingText(null);
        }
      }
    };

    fireSubmit();
  }, [pendingText, assistantInput.value, assistantInput.isDisabled, assistantInput.submit, thread, cancelRun, isStuck]);

  if (!store && !isStreaming) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
        <p className="text-xs font-black text-gray-400 uppercase tracking-widest">{t('merchant.no_info')}</p>
      </div>
    );
  }

  const handleSetActive = () => {
    if (!store?.name || assistantInput.isDisabled) return;
    const text = t('chat.set_merchant_intent', { name: store.name });
    assistantInput.setValue(text);
    setPendingText(text);
  };

  return (
    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="p-6 bg-emerald-500 text-white flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-sm shadow-inner text-white">
            <Store size={20} />
          </div>
          <div className="min-w-0">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-100 mb-0.5 opacity-80 leading-none">{t('merchant.found')}</h4>
            <p className="text-base font-black text-white leading-tight">{store?.name || t('history.local_store')}</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5 bg-white">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600 shrink-0">
            <MapPin size={16} />
          </div>
          <div>
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">{t('merchant.address')}</p>
            <p className="text-sm font-bold text-[#1a2b3c] leading-snug">{store?.address || t('merchant.no_info')}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <a 
            href={store?.url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((store?.name || '') + ' ' + (store?.address || ''))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 py-3 bg-emerald-50 rounded-xl text-[10px] font-black uppercase tracking-widest text-emerald-600 hover:bg-emerald-100 transition-colors"
          >
            <Navigation size={12} /> Google Maps
          </a>
          <button 
            onClick={handleSetActive}
            disabled={assistantInput.isDisabled}
            className="flex items-center justify-center gap-2 py-3 bg-[#1a2b3c] rounded-xl text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-gray-200 active:scale-95 transition-all disabled:opacity-50"
          >
            <CheckCircle2 size={12} /> {t('merchant.set_store')}
          </button>
        </div>
      </div>
    </div>
  );
};
